import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getAppSocket } from '../lib/wesSocket';
import {
  ArrowLeftIcon,
  BanknotesIcon,
  CreditCardIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  PrinterIcon,
  EyeIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { confirmDialog } from '../components/ConfirmDialog';
import { Button } from '../components/ui/button';
import { Tip } from '../components/ui/tip';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { openPdf } from '../utils/pdfHelpers';
import { hoyLocal, toYMD } from '../utils/fechaLocal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DatePicker } from '../components/ui/date-picker';

interface CurrentAccount {
  id: string;
  clientId?: string;
  entityId: string;
  entityType: 'client' | 'provider';
  creditLimit: number;
  currentBalance: number;
  status: string;
  notes: string;
  firstName?: string;
  lastName?: string;
  providerName?: string;
  companyName?: string;
  phone?: string;
  email?: string;
  displayName?: string;
  createdAt: string;
  updatedAt: string;
  linked?: {
    entityType: 'client' | 'provider';
    entityId: string;
    name: string;
    accountId: string | null;
    balance: number;
    autoCompensate: boolean;
  } | null;
}

interface Movement {
  id: string;
  accountId: string;
  type: 'charge' | 'payment' | 'credit' | 'debit' | 'adjustment';
  amount: number;
  description: string;
  reference?: string;
  invoiceId?: string;
  budgetId?: string;
  paymentMethod?: string;
  previousBalance: number;
  newBalance: number;
  createdBy: string;
  createdByName?: string;
  createdByLastName?: string;
  createdAt: string;
  // Vista unificada (cliente+proveedor vinculados)
  sourceEntityType?: 'client' | 'provider';
  sourceAccountId?: string;
  netoAfter?: number;
}

// Los movimientos cargados con fecha de otro día se guardan al mediodía (para
// que la fecha no se corra de día); esas 12:00 no son una hora real, así que no
// se muestran. Los segundos 00-59 también son relleno: se usan para ordenar
// movimientos cargados el mismo día.
const esFechaSinHora = (valor: string) => /^\d{4}-\d{2}-\d{2}([ T]12:00:\d{2}(\.0+)?Z?)?$/.test(String(valor || ''));

const CurrentAccountDetail: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [account, setAccount] = useState<CurrentAccount | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [linkedMovements, setLinkedMovements] = useState<Movement[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';


  // Movimientos de la otra punta del vínculo (para la línea de tiempo unificada).
  // Depende de `movements` para refrescarse junto con la cuenta principal.
  useEffect(() => {
    const linkedAccountId = account?.linked?.accountId;
    if (!linkedAccountId) {
      setLinkedMovements([]);
      return;
    }
    axios.get(`/api/current-accounts/${linkedAccountId}/movements`)
      .then((res) => setLinkedMovements(res.data.movements || []))
      .catch(() => setLinkedMovements([]));
  }, [account?.linked?.accountId, movements]);

  useEffect(() => {
    if (accountId) {
      fetchAccount();
      fetchMovements();
    }
  }, [accountId]);

  const realtimeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/current-accounts') ||
          path.startsWith('/api/remitos') ||
          path.startsWith('/api/invoices') ||
          path.startsWith('/api/checks')
        )
      ) {
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        if (!accountId) return;
        fetchAccount();
        fetchMovements();
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [accountId]);

  const fetchAccount = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/current-accounts/${accountId}`);
      setAccount(response.data.account);
    } catch (error) {
      toast.error('Error al cargar cuenta corriente');
      navigate('/current-accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`/api/current-accounts/${accountId}/movements`);
      setMovements(response.data.movements);
    } catch (error) {
      toast.error('Error al cargar movimientos');
    }
  };

  // Un cargo de cta cte guarda el número de remito, no la factura. El server
  // resuelve remito → factura (contemplando las que agrupan varios remitos) y
  // si todavía no se facturó devuelve el remito, que es el comprobante que
  // existe. Acá se pide el PDF que corresponda.
  const [printingInvoice, setPrintingInvoice] = useState<string | null>(null);
  const handlePrintInvoice = async (movement: Movement) => {
    setPrintingInvoice(movement.id);
    try {
      const res = await axios.get(
        `/api/current-accounts/${movement.sourceAccountId || accountId}/movements/${movement.id}/invoice`
      );
      const { tipo, id, numero } = res.data;
      const url = tipo === 'factura'
        ? `/api/invoices/${id}/generate-pdf`
        : `/api/remitos/${id}/generate-pdf`;
      const pdf = await axios.post(url, {}, { responseType: 'blob' });
      openPdf({ blob: pdf.data, filename: `${tipo === 'factura' ? 'Factura' : 'Remito'}-${numero}.pdf` });
      if (tipo === 'remito') toast.info(`Todavía no está facturado: se abre el remito ${numero}`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo abrir el comprobante');
    } finally {
      setPrintingInvoice(null);
    }
  };

  const handlePrintReceipt = async (movement: Movement) => {
    if (movement.type !== 'payment') {
      toast.error('Solo se pueden imprimir recibos de pagos');
      return;
    }

    try {
      const response = await fetch(`/api/current-accounts/${movement.sourceAccountId || accountId}/movements/${movement.id}/receipt`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const filename = `Recibo-${movement.id}-${new Date().toISOString().split('T')[0]}.pdf`;
        const result = openPdf({ blob, filename });
        toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abriendo en nueva pestaña');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en la respuesta del servidor');
      }
    } catch (error: any) {
      console.error('Error generando recibo:', error);
      toast.error(`Error al generar el recibo: ${error.message}`);
    }
  };

  const [downloadingStatement, setDownloadingStatement] = useState(false);
  // Período del estado de cuenta. Vacío = toda la historia. Al recortar, el
  // PDF arrastra el saldo anterior para que las columnas cierren.
  const [showPeriodo, setShowPeriodo] = useState(false);
  const [periodoDesde, setPeriodoDesde] = useState('');
  const [periodoHasta, setPeriodoHasta] = useState('');
  // Hay ciclo abierto si en algún momento la cuenta llegó a cero y después se
  // volvió a mover: si nunca se saldó, "desde el último saldo en 0" sería todo.
  const hayCicloAbierto = movements.some((m) => Math.abs(Number(m.newBalance) || 0) < 0.01)
    && Math.abs(Number(account?.currentBalance) || 0) >= 0.01;

  const handleDownloadStatement = async (desde = '', hasta = '', desdeSaldoCero = false) => {
    if (downloadingStatement) return;
    setShowPeriodo(false);
    setDownloadingStatement(true);
    try {
      const response = await fetch(`/api/current-accounts/${accountId}/movements-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          desde: desde || undefined,
          hasta: hasta || undefined,
          desdeSaldoCero: desdeSaldoCero || undefined
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const name = (account ? getDisplayName(account) : 'cuenta').replace(/[^a-zA-Z0-9]/g, '_') || 'cuenta';
        const filename = `Movimientos-${name}-${hoyLocal()}.pdf`;
        const result = openPdf({ blob, filename });
        toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abriendo en nueva pestaña');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error en la respuesta del servidor');
      }
    } catch (error: any) {
      console.error('Error generando PDF de movimientos:', error);
      toast.error(`Error al generar el PDF: ${error.message}`);
    } finally {
      setDownloadingStatement(false);
    }
  };

  // Abrir el documento de origen del movimiento: remito (R....), compra (OC....),
  // factura (invoiceId) o presupuesto (budgetId) según lo que tenga el movimiento.
  const remitoRef = (movement: Movement) => {
    const ref = movement.reference || '';
    return /^R\d{4}-\d+/.test(ref) ? ref : '';
  };
  const purchaseRef = (movement: Movement) => {
    const ref = movement.reference || '';
    return /^OC\d{4}-\d+/.test(ref) ? ref : '';
  };
  const sourceLabel = (movement: Movement) => {
    if (remitoRef(movement)) return 'Ver remito';
    if (purchaseRef(movement)) return 'Ver compra';
    return 'Ver documento';
  };
  const openMovementSource = (movement: Movement) => {
    const remito = remitoRef(movement);
    if (remito) { navigate(`/remitos?q=${encodeURIComponent(remito)}`); return; }
    const compra = purchaseRef(movement);
    if (compra) { navigate(`/purchases?q=${encodeURIComponent(compra)}`); return; }
    if (movement.invoiceId) { navigate(`/invoices?q=${encodeURIComponent(movement.reference || movement.invoiceId)}`); return; }
    if (movement.budgetId) { navigate(`/budgets?q=${encodeURIComponent(movement.reference || movement.budgetId)}`); return; }
    toast.info('Este movimiento no tiene un documento asociado para ver');
  };
  const movementHasSource = (movement: Movement) => !!(remitoRef(movement) || purchaseRef(movement) || movement.invoiceId || movement.budgetId);

  const getMovementIcon = (type: string) => {
    switch (type) {
      case 'charge':
      case 'debit':
        return <ArrowUpIcon className="h-4 w-4 text-red-500" />;
      case 'payment':
      case 'credit':
        return <ArrowDownIcon className="h-4 w-4 text-green-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getMovementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      charge: 'Cargo',
      payment: 'Pago',
      credit: 'Crédito',
      debit: 'Débito',
      adjustment: 'Ajuste'
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getBalanceColor = (balance: number, creditLimit: number) => {
    if (balance === 0) return 'text-gray-600 dark:text-gray-400';
    if (balance > 0 && creditLimit > 0 && balance > creditLimit) return 'text-red-600 dark:text-red-400';
    if (balance > 0) return 'text-orange-600 dark:text-orange-400';
    return 'text-green-600 dark:text-green-400';
  };

  const formatDate = (value?: string | null) => {
    if (!value) return 'Sin datos';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDisplayName = (account: CurrentAccount) => {
    if (account.entityType === 'provider') {
      return account.companyName || account.providerName || 'Proveedor sin nombre';
    } else {
      return account.companyName || `${account.firstName || ''} ${account.lastName || ''}`.trim() || 'Cliente sin nombre';
    }
  };

  // Línea de tiempo unificada: movimientos de ambas cuentas del vínculo,
  // ordenados por fecha, con el saldo NETO (cliente - proveedor) corriendo.
  const isLinked = Boolean(account?.linked?.accountId);
  const ownEntityType = account?.entityType || 'client';
  const mergedMovements: Movement[] = React.useMemo(() => {
    const own = movements.map((m) => ({ ...m, sourceEntityType: ownEntityType, sourceAccountId: accountId }));
    if (!isLinked || !account?.linked?.accountId) return own;
    const linkedType: 'client' | 'provider' = ownEntityType === 'client' ? 'provider' : 'client';
    const other = linkedMovements.map((m) => ({
      ...m,
      sourceEntityType: linkedType,
      sourceAccountId: account.linked!.accountId as string
    }));
    const all = [...own, ...other].sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || String(a.id).localeCompare(String(b.id))
    );
    let cliBal = 0;
    let provBal = 0;
    const withNeto = all.map((m) => {
      if (m.sourceEntityType === 'client') cliBal = m.newBalance;
      else provBal = m.newBalance;
      return { ...m, netoAfter: cliBal - provBal };
    });
    return withNeto.reverse();
  }, [movements, linkedMovements, isLinked, ownEntityType, accountId, account]);

  const totalCharges = movements.reduce((acc, mov) => {
    if (mov.type === 'charge' || mov.type === 'debit') return acc + mov.amount;
    return acc;
  }, 0);
  const totalPayments = movements.reduce((acc, mov) => {
    if (mov.type === 'payment' || mov.type === 'credit') return acc + mov.amount;
    return acc;
  }, 0);
  const lastChargeAt = movements.find((mov) => mov.type === 'charge' || mov.type === 'debit')?.createdAt || null;
  const lastPaymentAt = movements.find((mov) => mov.type === 'payment' || mov.type === 'credit')?.createdAt || null;
  const totalDebt = account ? Math.max(account.currentBalance, 0) : 0;
  const debtAgeDays = totalDebt > 0 && lastChargeAt
    ? Math.floor((Date.now() - new Date(lastChargeAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const notDueDebt = totalDebt > 0 && debtAgeDays !== null && debtAgeDays <= 30 ? totalDebt : 0;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-muted/30 border-t-primary"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Cuenta no encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/current-accounts')}
            className="shrink-0"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Cuenta Corriente</h2>
            <p className="text-sm text-muted-foreground">{getDisplayName(account)}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" onClick={() => setShowPeriodo(true)} disabled={downloadingStatement} className="gap-1.5">
            <ArrowDownTrayIcon className="h-4 w-4" />
            {downloadingStatement ? 'Generando...' : 'Descargar PDF'}
          </Button>
          {/* Registrar pago con ESTA cuenta ya pre-seleccionada (evita pagarle a
              la cuenta equivocada por dejar el selector en otra) */}
          <Button onClick={() => navigate(`/current-accounts/pay?accountId=${accountId}`)} className="gap-1.5">
            <BanknotesIcon className="h-4 w-4" />
            Registrar pago
          </Button>
        </div>
      </div>

      {account.linked && (
        <Card className="border-blue-500/30">
          <CardContent className="py-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 font-medium">
                ⇄ {account.entityType === 'client' ? 'También es proveedor' : 'También es cliente'}
              </span>
              {(() => {
                const cliBal = account.entityType === 'client' ? account.currentBalance : account.linked!.balance;
                const provBal = account.entityType === 'provider' ? account.currentBalance : account.linked!.balance;
                const neto = cliBal - provBal;
                return (
                  <>
                    <span className="text-muted-foreground">Como cliente te debe: <span className="text-foreground font-medium">{formatCurrency(cliBal)}</span></span>
                    <span className="text-muted-foreground">Como proveedor le debés: <span className="text-foreground font-medium">{formatCurrency(provBal)}</span></span>
                    <span className={`font-semibold ${neto > 0.009 ? 'text-emerald-500' : neto < -0.009 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                      Neto: {formatCurrency(neto)} {neto > 0.009 ? 'a tu favor' : neto < -0.009 ? 'en contra' : ''}
                    </span>
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-1 rounded-lg border p-1 self-start lg:self-auto">
              <Button
                type="button"
                size="sm"
                variant={account.entityType === 'client' ? 'default' : 'ghost'}
                className="h-7 px-3"
                onClick={() => {
                  if (account.entityType !== 'client' && account.linked?.accountId) navigate(`/current-accounts/${account.linked.accountId}`);
                }}
              >
                Como cliente
              </Button>
              <Button
                type="button"
                size="sm"
                variant={account.entityType === 'provider' ? 'default' : 'ghost'}
                className="h-7 px-3"
                onClick={() => {
                  if (account.entityType !== 'provider' && account.linked?.accountId) navigate(`/current-accounts/${account.linked.accountId}`);
                }}
              >
                Como proveedor
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="space-y-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Balance actual
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-2">
            <p className={`text-2xl font-semibold ${getBalanceColor(account.currentBalance, account.creditLimit)}`}>
              {formatCurrency(account.currentBalance)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <CreditCardIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Límite de crédito
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-2">
            <p className="text-2xl font-semibold text-foreground">
              {formatCurrency(account.creditLimit)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Disponible
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-2">
            <p className="text-2xl font-semibold text-primary">
              {formatCurrency(Math.max(0, account.creditLimit - account.currentBalance))}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="space-y-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total adeudado
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-2">
            <p className={`text-2xl font-semibold ${totalDebt > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-foreground'}`}>
              {formatCurrency(totalDebt)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <ClockIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                No vencido
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-2">
            <p className="text-2xl font-semibold text-foreground">
              {formatCurrency(notDueDebt)}
            </p>
            {totalDebt > 0 && debtAgeDays !== null && debtAgeDays > 30 && (
              <p className="text-xs text-destructive">Deuda vencida</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <ArrowDownIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pagos parciales
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-2">
            <p className="text-2xl font-semibold text-foreground">
              {formatCurrency(totalPayments)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <CreditCardIcon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Ultimos movimientos
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-2 space-y-1">
            <p className="text-sm text-muted-foreground">Ultima compra: <span className="text-foreground">{formatDate(lastChargeAt)}</span></p>
            <p className="text-sm text-muted-foreground">Ultimo pago: <span className="text-foreground">{formatDate(lastPaymentAt)}</span></p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Movimientos</CardTitle>
        </CardHeader>
        <CardContent>
          {mergedMovements.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground">
              <ClockIcon className="h-10 w-10 text-muted-foreground/60" />
              <p>No hay movimientos registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-b hover:bg-muted/50">
                    <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                    {isLinked && (
                      <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Cuenta</TableHead>
                    )}
                    <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                    <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Descripción</TableHead>
                    <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Método</TableHead>
                    <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-right">Monto</TableHead>
                    <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-right">{isLinked ? 'Saldo neto' : 'Balance'}</TableHead>
                    <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mergedMovements.map((movement) => (
                    <TableRow
                      key={movement.id}
                      onClick={() => movementHasSource(movement) && openMovementSource(movement)}
                      className={`border-b last:border-0 hover:bg-muted/50 ${movementHasSource(movement) ? 'cursor-pointer' : ''}`}
                    >
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        <div className="text-sm">
                          {new Date(movement.createdAt).toLocaleDateString('es-AR')}
                        </div>
                        {!esFechaSinHora(movement.createdAt) && (
                          <div className="text-xs text-muted-foreground">
                            {new Date(movement.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </TableCell>
                      {isLinked && (
                        <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                          <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${
                            movement.sourceEntityType === 'client'
                              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                              : 'bg-violet-500/15 text-violet-600 dark:text-violet-400'
                          }`}>
                            {movement.sourceEntityType === 'client' ? 'Cliente' : 'Proveedor'}
                          </span>
                        </TableCell>
                      )}
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        <div className="flex items-center gap-2">
                          {getMovementIcon(movement.type)}
                          <Badge variant="outline">
                            {getMovementTypeLabel(movement.type)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle text-left">
                        <div className="font-medium text-foreground">{movement.description}</div>
                        {movement.reference && (
                          <div className="text-xs text-muted-foreground">Ref: {movement.reference}</div>
                        )}
                        {movement.createdByName && (
                          <div className="text-xs text-muted-foreground">
                            Por: {movement.createdByName} {movement.createdByLastName || ''}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        {movement.paymentMethod === 'compensacion' ? (
                          <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400">⇄ Compensación</span>
                        ) : movement.paymentMethod ? (
                          <span className="text-sm capitalize">{movement.paymentMethod.replace('_', ' ')}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-right">
                        <span
                          className={`font-semibold ${
                            movement.type === 'charge' || movement.type === 'debit'
                              ? 'text-destructive'
                              : 'text-emerald-500'
                          }`}
                        >
                          {movement.type === 'charge' || movement.type === 'debit' ? '+' : '-'}
                          {formatCurrency(movement.amount)}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-right">
                        {isLinked ? (
                          <span className={`font-medium ${Number(movement.netoAfter || 0) < -0.009 ? 'text-orange-500' : ''}`}>
                            {formatCurrency(Number(movement.netoAfter || 0))}
                          </span>
                        ) : (
                          <span className="font-medium">{formatCurrency(movement.newBalance)}</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          {movementHasSource(movement) && (
                            <Tip label={sourceLabel(movement)}>
                              <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={(e) => { e.stopPropagation(); openMovementSource(movement); }}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          )}
                          {movement.type === 'charge' && !!movement.reference && movement.reference !== 'saldo-inicial' && (
                            <Tip label="Imprimir la factura de este cargo (o el remito, si todavía no se facturó)">
                              <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                disabled={printingInvoice === movement.id}
                                onClick={(e) => { e.stopPropagation(); handlePrintInvoice(movement); }}
                              >
                                <PrinterIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          )}
                          {movement.type === 'payment' && (
                            <Tip label="Imprimir recibo">
                              <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={(e) => { e.stopPropagation(); handlePrintReceipt(movement); }}
                              >
                                <PrinterIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          )}
                          {isAdmin
                            && (movement.type !== 'charge' || movement.reference === 'saldo-inicial')
                            && movement.paymentMethod !== 'compensacion' && (
                            <Tip label="Editar movimiento">
                              <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const acc = movement.sourceAccountId || accountId;
                                  navigate(`/current-accounts/pay?accountId=${acc}&movementId=${movement.id}`);
                                }}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          )}
                          {(movement.type !== 'charge' || movement.reference === 'saldo-inicial') && (
                            <Tip label="Eliminar movimiento">
                              <Button
                                variant="outline"
                                size="icon"
                                className="size-8 text-muted-foreground hover:text-red-500"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const esCheque = (movement.paymentMethod || '') === 'cheque';
                                  const esCompensacion = (movement.paymentMethod || '') === 'compensacion';
                                  const esSaldoInicial = movement.reference === 'saldo-inicial';
                                  const fmt = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(movement.amount);
                                  const ok = await confirmDialog({
                                    title: 'Eliminar movimiento',
                                    description: esCompensacion
                                      ? `¿Eliminar esta compensación de ${fmt}? Se elimina el par completo (este movimiento y su espejo en la otra cuenta corriente) y ambos saldos se recalculan.`
                                      : esSaldoInicial
                                        ? `¿Eliminar el saldo inicial de ${fmt}? El saldo de la cuenta se recalcula.`
                                        : `¿Eliminar este ${movement.type === 'payment' ? 'pago' : 'movimiento'} de ${fmt}? El saldo de la cuenta se recalcula.${esCheque ? ' Los cheques en cartera de este pago que sigan pendientes también se eliminan.' : ''}`,
                                    confirmText: 'Eliminar',
                                  });
                                  if (!ok) return;
                                  try {
                                    const res = await axios.delete(`/api/current-accounts/${movement.sourceAccountId || accountId}/movements/${movement.id}`);
                                    const n = res.data?.deletedChecks || 0;
                                    toast.success(`Movimiento eliminado${n ? ` (y ${n} cheque${n > 1 ? 's' : ''} en cartera)` : ''}`);
                                    fetchAccount();
                                    fetchMovements();
                                  } catch (err: any) {
                                    toast.error(err.response?.data?.error || 'Error al eliminar');
                                  }
                                }}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Qué período llevar al PDF. Los atajos cubren lo que se pide casi
          siempre; el rango a mano queda para el resto. */}
      <Dialog open={showPeriodo} onOpenChange={setShowPeriodo}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Estado de cuenta</DialogTitle>
            <DialogDescription>
              Elegí qué período llevar al PDF. Si recortás, arriba de la tabla va el saldo anterior
              para que las columnas cierren.
            </DialogDescription>
          </DialogHeader>

          {/* Lo que se debe desde que se saldó: el ciclo de deuda actual, que es
              lo que uno le manda al cliente para reclamarle. */}
          {hayCicloAbierto && (
            <Button className="w-full" onClick={() => handleDownloadStatement('', '', true)}>
              Desde el último saldo en 0
            </Button>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => handleDownloadStatement()}>Toda la cuenta</Button>
            <Button variant="outline" onClick={() => {
              const h = new Date();
              handleDownloadStatement(toYMD(new Date(h.getFullYear(), h.getMonth(), 1)), hoyLocal());
            }}>Este mes</Button>
            <Button variant="outline" onClick={() => {
              const h = new Date();
              handleDownloadStatement(toYMD(new Date(h.getFullYear(), h.getMonth() - 1, 1)), toYMD(new Date(h.getFullYear(), h.getMonth(), 0)));
            }}>Mes pasado</Button>
            <Button variant="outline" onClick={() => {
              const h = new Date();
              handleDownloadStatement(toYMD(new Date(h.getFullYear(), h.getMonth() - 2, 1)), hoyLocal());
            }}>Últimos 3 meses</Button>
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-sm font-medium">Período personalizado</p>
            <div className="flex items-center gap-2">
              <DatePicker value={periodoDesde} onChange={setPeriodoDesde} placeholder="Desde" className="h-9 flex-1" />
              <DatePicker value={periodoHasta} onChange={setPeriodoHasta} placeholder="Hasta" className="h-9 flex-1" />
            </div>
            <Button
              className="w-full"
              disabled={!periodoDesde && !periodoHasta}
              onClick={() => handleDownloadStatement(periodoDesde, periodoHasta)}
            >
              Descargar ese período
            </Button>
            <p className="text-xs text-muted-foreground">
              Se puede dejar una de las dos: solo "Desde" lleva hasta hoy, solo "Hasta" arranca del principio.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrentAccountDetail;
